// ─── Task Types ───────────────────────────────────────────────────────────────
export type TaskCategory = 'feeding' | 'health_check' | 'cleaning' | 'vaccination' | 'egg_collection' | 'weight_check' | 'biosecurity' | 'other';
export type TaskStatus = 'pending' | 'in_progress' | 'completed' | 'overdue' | 'skipped';
export type TaskPriority = 'low' | 'medium' | 'high' | 'critical';

export interface FarmTask {
  id: string;
  title: string;
  description: string;
  category: TaskCategory;
  status: TaskStatus;
  priority: TaskPriority;
  assignedTo: string;
  assignedToInitials: string;
  farm: string;
  house?: string;
  flock?: string;
  scheduledTime: string;
  completedAt?: string;
  notes?: string;
  date: string;
  createdBy: string;
}

// ─── Category Config ──────────────────────────────────────────────────────────
export const CATEGORY_CONFIG: Record<TaskCategory, { label: string; color: string; bg: string; icon: string }> = {
  feeding:      { label: 'Feeding',       color: 'var(--warning)',  bg: 'var(--warning-bg)',  icon: 'wheat' },
  health_check: { label: 'Health Check',  color: 'var(--info)',     bg: 'var(--info-bg)',     icon: 'stethoscope' },
  cleaning:     { label: 'Cleaning',      color: '#8b5cf6',         bg: '#f5f3ff',            icon: 'sparkles' },
  vaccination:  { label: 'Vaccination',   color: 'var(--danger)',   bg: '#fef2f2',            icon: 'syringe' },
  egg_collection:{ label: 'Egg Collection', color: '#f59e0b',       bg: '#fffbeb',            icon: 'egg' },
  weight_check: { label: 'Weight Check',  color: 'var(--primary)',  bg: '#f0fdf4',            icon: 'scale' },
  biosecurity:  { label: 'Biosecurity',   color: '#0891b2',         bg: '#ecfeff',            icon: 'shield' },
  other:        { label: 'Other',         color: 'var(--muted-foreground)', bg: 'var(--muted)', icon: 'clipboard' },
};

export const STATUS_CONFIG: Record<TaskStatus, { label: string; color: string; bg: string }> = {
  pending:     { label: 'Pending',     color: 'var(--muted-foreground)', bg: 'var(--muted)' },
  in_progress: { label: 'In Progress', color: 'var(--info)',             bg: 'var(--info-bg)' },
  completed:   { label: 'Completed',   color: 'var(--success)',          bg: 'var(--success-bg)' },
  overdue:     { label: 'Overdue',     color: 'var(--danger)',           bg: '#fef2f2' },
  skipped:     { label: 'Skipped',     color: '#f59e0b',                 bg: '#fffbeb' },
};

export const PRIORITY_CONFIG: Record<TaskPriority, { label: string; color: string }> = {
  low:      { label: 'Low',      color: 'var(--muted-foreground)' },
  medium:   { label: 'Medium',   color: 'var(--warning)' },
  high:     { label: 'High',     color: 'var(--danger)' },
  critical: { label: 'Critical', color: '#dc2626' },
};

const today = new Date().toISOString().split('T')[0];

// ─── Mock Tasks ───────────────────────────────────────────────────────────────
export const MOCK_TASKS: FarmTask[] = [
  {
    id: 'task-001',
    title: 'Morning feeding — House A1 & A2',
    description: 'Distribute broiler starter feed. Check feed troughs and water nipples.',
    category: 'feeding',
    status: 'completed',
    priority: 'high',
    assignedTo: 'Ogenga Paul',
    assignedToInitials: 'OP',
    farm: 'Idudi Poultry Farm',
    house: 'House A1 & A2',
    flock: 'BR-2026-001',
    scheduledTime: '06:00',
    completedAt: '06:15',
    date: today,
    createdBy: 'Okello James',
  },
  {
    id: 'task-002',
    title: 'Egg collection — Layer House 01',
    description: 'Collect and count all eggs. Record breakages and soiled eggs separately.',
    category: 'egg_collection',
    status: 'completed',
    priority: 'high',
    assignedTo: 'Akello Grace',
    assignedToInitials: 'AG',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 01',
    flock: 'LY-2026-001',
    scheduledTime: '06:30',
    completedAt: '06:50',
    date: today,
    createdBy: 'Okello James',
  },
  {
    id: 'task-003',
    title: 'Morning health check — all houses',
    description: 'Walk through all houses. Check for sick or dead birds, unusual behavior, respiratory signs.',
    category: 'health_check',
    status: 'completed',
    priority: 'critical',
    assignedTo: 'Okello James',
    assignedToInitials: 'OJ',
    farm: 'Idudi Poultry Farm',
    scheduledTime: '07:00',
    completedAt: '07:45',
    notes: 'Found 3 lethargic birds in House B2. Isolated and reported to vet.',
    date: today,
    createdBy: 'Okello James',
  },
  {
    id: 'task-004',
    title: 'Weight sampling — Flock BR-2026-002',
    description: 'Sample 30 birds for body weight. Record individual weights and calculate average.',
    category: 'weight_check',
    status: 'in_progress',
    priority: 'medium',
    assignedTo: 'Ogenga Paul',
    assignedToInitials: 'OP',
    farm: 'Idudi Poultry Farm',
    house: 'House B1',
    flock: 'BR-2026-002',
    scheduledTime: '08:00',
    date: today,
    createdBy: 'Okello James',
  },
  {
    id: 'task-005',
    title: 'Newcastle vaccination — Flock LY-2026-003',
    description: 'Administer Newcastle Disease vaccine via drinking water. Withhold water 2 hrs before.',
    category: 'vaccination',
    status: 'pending',
    priority: 'critical',
    assignedTo: 'Dr. Nakato Sarah',
    assignedToInitials: 'NS',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 03',
    flock: 'LY-2026-003',
    scheduledTime: '10:00',
    date: today,
    createdBy: 'Okello James',
  },
  {
    id: 'task-006',
    title: 'Midday feeding — all broiler houses',
    description: 'Second feeding round. Ensure all feeders are full and water is clean.',
    category: 'feeding',
    status: 'pending',
    priority: 'high',
    assignedTo: 'Ogenga Paul',
    assignedToInitials: 'OP',
    farm: 'Idudi Poultry Farm',
    scheduledTime: '12:00',
    date: today,
    createdBy: 'Okello James',
  },
  {
    id: 'task-007',
    title: 'Footbath change — House B1 & B2',
    description: 'Replace footbath solution with fresh disinfectant. Clean footbath trays.',
    category: 'biosecurity',
    status: 'overdue',
    priority: 'high',
    assignedTo: 'Apio Christine',
    assignedToInitials: 'AC',
    farm: 'Idudi Poultry Farm',
    house: 'House B1 & B2',
    scheduledTime: '09:00',
    date: today,
    createdBy: 'Okello James',
  },
  {
    id: 'task-008',
    title: 'Afternoon egg collection — Layer House 02',
    description: 'Second egg collection of the day. Grade and pack eggs for storage.',
    category: 'egg_collection',
    status: 'pending',
    priority: 'medium',
    assignedTo: 'Akello Grace',
    assignedToInitials: 'AG',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 02',
    flock: 'LY-2026-002',
    scheduledTime: '16:30',
    date: today,
    createdBy: 'Okello James',
  },
  {
    id: 'task-009',
    title: 'Deep clean — House C1 (empty)',
    description: 'Full litter removal, pressure wash walls and floor, disinfect with Virkon S.',
    category: 'cleaning',
    status: 'pending',
    priority: 'medium',
    assignedTo: 'Apio Christine',
    assignedToInitials: 'AC',
    farm: 'Idudi Poultry Farm',
    house: 'House C1',
    scheduledTime: '13:00',
    date: today,
    createdBy: 'Okello James',
  },
  {
    id: 'task-010',
    title: 'Evening health check — Layer houses',
    description: 'End-of-day health inspection. Record mortality, check ventilation settings.',
    category: 'health_check',
    status: 'pending',
    priority: 'high',
    assignedTo: 'Okello James',
    assignedToInitials: 'OJ',
    farm: 'Idudi Poultry Farm',
    scheduledTime: '17:30',
    date: today,
    createdBy: 'Okello James',
  },
  {
    id: 'task-011',
    title: 'Morning feeding — Kampala broilers',
    description: 'Feed all broiler pens. Check water pressure and drinker levels.',
    category: 'feeding',
    status: 'completed',
    priority: 'high',
    assignedTo: 'Mugisha Robert',
    assignedToInitials: 'MR',
    farm: 'Kampala Demo Farm',
    scheduledTime: '06:00',
    completedAt: '06:20',
    date: today,
    createdBy: 'Okello James',
  },
  {
    id: 'task-012',
    title: 'Gumboro vaccination — Flock KA-2026-001',
    description: 'Administer IBD (Gumboro) vaccine. Record batch number and expiry.',
    category: 'vaccination',
    status: 'in_progress',
    priority: 'critical',
    assignedTo: 'Dr. Nakato Sarah',
    assignedToInitials: 'NS',
    farm: 'Kampala Demo Farm',
    flock: 'KA-2026-001',
    scheduledTime: '09:30',
    date: today,
    createdBy: 'Okello James',
  },
];
